/** Chain timeline — one row per AuditEvent, in sequence order. Each row
 * collapses to a one-line summary; clicking expands the full payload +
 * hash pair so an auditor can inspect any step. */
import { useState } from "react";
import { formatTime } from "@/lib/format";
import { useTranslation } from "react-i18next";

import type { AuditEventRow } from "./types";


type Props = {
  events: AuditEventRow[];
  /** Sequence of a tampered event — that row renders in the danger style. */
  tamperedSequence?: number;
  truncated?: boolean;
};


export function ChainTimeline({ events, tamperedSequence, truncated }: Props) {
  const { t } = useTranslation();
  return (
    <section className="card space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("ui.chainTimeline.heading")}
        </h2>
        <span className="text-xs text-nb-text-3">
          {t("ui.chainTimeline.eventsCount", { count: events.length })}
        </span>
      </div>
      {events.length === 0 ? (
        <p className="py-2 text-sm text-nb-text-3">{t("ui.chainTimeline.noEvents")}</p>
      ) : (
        <ul className="divide-y divide-nb-border">
          {events.map((ev) => (
            <EventRow
              key={ev.id}
              ev={ev}
              tampered={ev.sequence === tamperedSequence}
            />
          ))}
        </ul>
      )}
      {truncated && (
        <p className="pt-2 text-xs text-cvp-warn">
          {t("ui.chainTimeline.truncated")}
        </p>
      )}
    </section>
  );
}


function EventRow({ ev, tampered }: { ev: AuditEventRow; tampered: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const actor =
    ev.actor_user_email ??
    (ev.actor_participant_id ? `participant:${ev.actor_participant_id.slice(0, 8)}` : "system");
  const rowClass = tampered
    ? "bg-cvp-danger/10"
    : "hover:bg-nb-surface/40";

  return (
    <li className={`py-2 transition ${rowClass}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-3 px-2 text-left"
        aria-expanded={open}
        data-testid={`audit-event-row-${ev.sequence}`}
      >
        <span className="w-12 font-mono text-xs text-nb-text-3">
          #{ev.sequence}
        </span>
        <span className="flex-1 text-sm text-nb-text">
          <span className="font-medium">{ev.kind}</span>
          <span className="ml-2 text-xs text-nb-text-3">{actor}</span>
        </span>
        <span className="text-xs text-nb-text-3">
          {formatTime(ev.occurred_at)}
        </span>
        <code className="ml-2 hidden font-mono text-[10px] text-nb-text-3 sm:inline">
          {ev.hash.slice(0, 10)}…
        </code>
      </button>
      {open && (
        <div className="mt-2 space-y-2 border-l-2 border-nb-border px-3 py-2 text-xs">
          <Pair label={t("ui.chainTimeline.occurredAt")} value={new Date(ev.occurred_at).toISOString()} />
          <Pair label={t("ui.chainTimeline.prevHash")} value={ev.prev_hash} mono />
          <Pair label={t("ui.chainTimeline.hash")} value={ev.hash} mono />
          <div>
            <span className="block text-nb-text-3">{t("ui.chainTimeline.payload")}</span>
            <pre className="mt-1 overflow-x-auto rounded bg-slate-900/60 p-2 font-mono text-[11px] text-nb-text">
              {JSON.stringify(ev.payload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </li>
  );
}


function Pair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="w-24 text-nb-text-3">{label}</span>
      <span className={mono ? "break-all font-mono text-nb-text" : "text-nb-text"}>
        {value}
      </span>
    </div>
  );
}
